import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { isGuest } from '../lib/guestMode';

const TutorialContext = createContext(null);

// Tutorial keys
export const TUTORIAL_KEYS = {
  WELCOME: 'welcome',
  FIRST_CARD: 'first_card',
  FIRST_BINDER: 'first_binder',
  FIRST_POST: 'first_post',
  FIRST_TRADE: 'first_trade',
  LOCATIONS: 'locations',
  CHAT: 'chat',
};

// Tutorial steps for the "No thanks" flow
export const TUTORIAL_STEPS = {
  NONE: 'none',
  WELCOME: 'welcome',
  POINT_TO_PROFILE: 'point_to_profile',
  PROFILE_FAREWELL: 'profile_farewell',
  // "Yes, show me!" flow
  YES_INTRO_CHASE: 'yes_intro_chase',
  YES_CHASE_HELLO: 'yes_chase_hello',
  // Add Card/Binder flow
  ADD_CARD_BINDER_NAV: 'add_card_binder_nav', // Spotlight Cards in bottom nav
  ADD_CARD_BINDER_BUTTONS: 'add_card_binder_buttons', // Spotlight Add Card + Add Binder buttons
  // Post Card flow
  POST_CARD_NAV: 'post_card_nav', // Spotlight Post button in bottom nav (Home screen)
  // Congrats after adding card/binder/post
  FIRST_CARD_CONGRATS: 'first_card_congrats',
  FIRST_BINDER_CONGRATS: 'first_binder_congrats',
  FIRST_POST_CONGRATS: 'first_post_congrats',
  // First trade request tip (not part of guided tutorial)
  FIRST_TRADE_REQUEST_TIP: 'first_trade_request_tip',
  // Trade tutorial flow
  TRADE_FIND_CARD: 'trade_find_card', // Chase tells user to find a Trade button
  TRADE_LOOKING: 'trade_looking', // User is scrolling, arrows on Trade buttons
  TRADE_BLOCKED: 'trade_blocked', // User clicked Trade, Chase says it didn't work
  TRADE_CHAT_NAV: 'trade_chat_nav', // Spotlight Chat in bottom nav
  TRADE_CHAT_INTRO: 'trade_chat_intro', // Chase explains Chat dashboard
};

// Steps that block navigation
const BLOCKING_STEPS = [
  TUTORIAL_STEPS.WELCOME,
  TUTORIAL_STEPS.POINT_TO_PROFILE,
  TUTORIAL_STEPS.YES_INTRO_CHASE,
  TUTORIAL_STEPS.YES_CHASE_HELLO,
  TUTORIAL_STEPS.ADD_CARD_BINDER_NAV,
  TUTORIAL_STEPS.TRADE_FIND_CARD,
  TUTORIAL_STEPS.TRADE_BLOCKED,
  TUTORIAL_STEPS.TRADE_CHAT_NAV,
  TUTORIAL_STEPS.TRADE_CHAT_INTRO,
];

export const TutorialProvider = ({ children }) => {
  const { user } = useAuth();
  const [tutorialProgress, setTutorialProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [showWelcomeTutorial, setShowWelcomeTutorial] = useState(false);
  const [currentStep, setCurrentStep] = useState(TUTORIAL_STEPS.NONE);
  const [showProfileFarewell, setShowProfileFarewell] = useState(false);
  // Track if user is in the add card/binder tutorial flow (to trigger congrats after)
  const [isInAddCardBinderFlow, setIsInAddCardBinderFlow] = useState(false);
  // Track what they just added for congrats message
  const [lastAddedType, setLastAddedType] = useState(null); // 'card' or 'binder'
  // Hide my posts filter - used by tutorial and can be toggled in Profile
  const [hideMyPosts, setHideMyPosts] = useState(false);

  // Fetch tutorial progress from Supabase
  const fetchTutorialProgress = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('tutorial_progress')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      // Convert to object keyed by tutorial_key
      const progressMap = {};
      data?.forEach(item => {
        progressMap[item.tutorial_key] = item;
      });

      setTutorialProgress(progressMap);
    } catch (err) {
      console.error('Error fetching tutorial progress:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchTutorialProgress();
  }, [fetchTutorialProgress]);

  // Check if a tutorial has been shown/completed
  const hasTutorialBeenShown = useCallback((tutorialKey) => {
    const progress = tutorialProgress[tutorialKey];
    return progress?.status === 'shown' || progress?.status === 'completed' || progress?.status === 'declined';
  }, [tutorialProgress]);

  // Mark tutorial as shown
  const markTutorialShown = useCallback(async (tutorialKey) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('tutorial_progress')
        .upsert({
          user_id: user.id,
          tutorial_key: tutorialKey,
          status: 'shown',
          shown_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,tutorial_key',
        });

      if (error) throw error;

      setTutorialProgress(prev => ({
        ...prev,
        [tutorialKey]: {
          ...prev[tutorialKey],
          tutorial_key: tutorialKey,
          status: 'shown',
          shown_at: new Date().toISOString(),
        },
      }));
    } catch (err) {
      console.error('Error marking tutorial as shown:', err);
    }
  }, [user?.id]);

  // Mark tutorial as completed
  const markTutorialCompleted = useCallback(async (tutorialKey) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('tutorial_progress')
        .upsert({
          user_id: user.id,
          tutorial_key: tutorialKey,
          status: 'completed',
          completed_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,tutorial_key',
        });

      if (error) throw error;

      setTutorialProgress(prev => ({
        ...prev,
        [tutorialKey]: {
          ...prev[tutorialKey],
          tutorial_key: tutorialKey,
          status: 'completed',
          completed_at: new Date().toISOString(),
        },
      }));
    } catch (err) {
      console.error('Error marking tutorial as completed:', err);
    }
  }, [user?.id]);

  // Mark tutorial as declined
  const markTutorialDeclined = useCallback(async (tutorialKey) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('tutorial_progress')
        .upsert({
          user_id: user.id,
          tutorial_key: tutorialKey,
          status: 'declined',
          shown_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,tutorial_key',
        });

      if (error) throw error;

      setTutorialProgress(prev => ({
        ...prev,
        [tutorialKey]: {
          ...prev[tutorialKey],
          tutorial_key: tutorialKey,
          status: 'declined',
          shown_at: new Date().toISOString(),
        },
      }));
    } catch (err) {
      console.error('Error marking tutorial as declined:', err);
    }
  }, [user?.id]);

  // Reset all tutorials (for testing)
  const resetAllTutorials = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('tutorial_progress')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      setTutorialProgress({});
      setShowWelcomeTutorial(false);
    } catch (err) {
      console.error('Error resetting tutorials:', err);
      throw err;
    }
  }, [user?.id]);

  // Trigger welcome tutorial check
  const checkWelcomeTutorial = useCallback(() => {
    if (loading) return;
    // Guests don't see the welcome / Chef tutorial — they're browsing, not onboarding
    if (!user && isGuest()) return;

    const progress = tutorialProgress[TUTORIAL_KEYS.WELCOME];

    // If welcome was shown but not completed/declined, resume at point_to_profile
    if (progress?.status === 'shown') {
      setCurrentStep(TUTORIAL_STEPS.POINT_TO_PROFILE);
      return;
    }

    // If never shown, show welcome
    if (!progress) {
      setTimeout(() => {
        setShowWelcomeTutorial(true);
        setCurrentStep(TUTORIAL_STEPS.WELCOME);
      }, 500);
    }
  }, [loading, tutorialProgress]);

  // Handle welcome tutorial responses
  const handleWelcomeTutorialYes = useCallback(async () => {
    // Set the next step FIRST, then hide welcome after a tiny delay
    // This prevents the flash where no overlay is visible
    setCurrentStep(TUTORIAL_STEPS.YES_INTRO_CHASE);
    setTimeout(() => {
      setShowWelcomeTutorial(false);
    }, 50);
  }, []);

  // When user clicks Next after Chef introduces Chase
  const handleIntroChaseNext = useCallback(() => {
    setCurrentStep(TUTORIAL_STEPS.YES_CHASE_HELLO);
  }, []);

  // When user selects any option on Chase's screen
  const handleChaseOptionSelected = useCallback(async (option) => {
    // Mark welcome tutorial as complete for all options
    await markTutorialCompleted(TUTORIAL_KEYS.WELCOME);

    if (option === 'add_card_binder') {
      // Start the Add Card/Binder tutorial flow
      setCurrentStep(TUTORIAL_STEPS.ADD_CARD_BINDER_NAV);
    } else if (option === 'post_card') {
      // Start the Post Card tutorial flow - spotlight Post button on Home
      setCurrentStep(TUTORIAL_STEPS.POST_CARD_NAV);
    } else if (option === 'set_up_trade') {
      // Start the Trade tutorial flow
      setCurrentStep(TUTORIAL_STEPS.TRADE_FIND_CARD);
    } else {
      // For other options, just end tutorial for now
      setCurrentStep(TUTORIAL_STEPS.NONE);
    }
  }, [markTutorialCompleted]);

  // When user clicks Cards nav during ADD_CARD_BINDER_NAV step
  const handleCardsNavClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_NAV) {
      setCurrentStep(TUTORIAL_STEPS.ADD_CARD_BINDER_BUTTONS);
    }
  }, [currentStep]);

  // When user clicks Post button during POST_CARD_NAV step
  const handlePostNavClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.POST_CARD_NAV) {
      // Tutorial complete - user clicked the Post button
      setCurrentStep(TUTORIAL_STEPS.NONE);
    }
  }, [currentStep]);

  // When user clicks Add button during ADD_CARD_BINDER_BUTTONS step (starts flow)
  const handleAddButtonClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.ADD_CARD_BINDER_BUTTONS) {
      // User is in tutorial, set flag so we can show congrats after modal closes
      setIsInAddCardBinderFlow(true);
      setCurrentStep(TUTORIAL_STEPS.NONE); // Clear overlay while modal is open
    }
  }, [currentStep]);

  // When user successfully adds a card (called from Collection.js after modal onSuccess)
  // Shows congrats if first_card tutorial hasn't been completed yet
  const handleCardAdded = useCallback(() => {
    if (!hasTutorialBeenShown(TUTORIAL_KEYS.FIRST_CARD)) {
      // Delay so user can see their new card first
      setTimeout(() => {
        setLastAddedType('card');
        setCurrentStep(TUTORIAL_STEPS.FIRST_CARD_CONGRATS);
      }, 1000);
    }
  }, [hasTutorialBeenShown]);

  // When user successfully adds a binder (called from Collection.js after modal onCreateBinder)
  // Shows congrats if first_binder tutorial hasn't been completed yet
  const handleBinderAdded = useCallback(() => {
    if (!hasTutorialBeenShown(TUTORIAL_KEYS.FIRST_BINDER)) {
      // Delay so user can see their new binder first
      setTimeout(() => {
        setLastAddedType('binder');
        setCurrentStep(TUTORIAL_STEPS.FIRST_BINDER_CONGRATS);
      }, 1000);
    }
  }, [hasTutorialBeenShown]);

  // When user successfully creates a post (called from CreatePostModal after success)
  // Shows congrats if first_post tutorial hasn't been completed yet
  const handlePostAdded = useCallback(() => {
    if (!hasTutorialBeenShown(TUTORIAL_KEYS.FIRST_POST)) {
      // Delay so user can see their post first
      setTimeout(() => {
        setLastAddedType('post');
        setCurrentStep(TUTORIAL_STEPS.FIRST_POST_CONGRATS);
      }, 1000);
    }
  }, [hasTutorialBeenShown]);

  // When user sends their first trade request (outside of tutorial)
  // Shows tip about where to find their requests
  const handleTradeRequestSent = useCallback(() => {
    console.log('handleTradeRequestSent called');
    console.log('FIRST_TRADE shown?', hasTutorialBeenShown(TUTORIAL_KEYS.FIRST_TRADE));
    console.log('tutorialProgress:', tutorialProgress);
    if (!hasTutorialBeenShown(TUTORIAL_KEYS.FIRST_TRADE)) {
      console.log('Showing first trade tip!');
      // Delay so user can see the request was sent
      setTimeout(() => {
        setCurrentStep(TUTORIAL_STEPS.FIRST_TRADE_REQUEST_TIP);
      }, 1000);
    }
  }, [hasTutorialBeenShown, tutorialProgress]);

  // When user clicks "Next" on the first trade request tip - show Chat nav
  const handleFirstTradeRequestNext = useCallback(async () => {
    await markTutorialCompleted(TUTORIAL_KEYS.FIRST_TRADE);
    setCurrentStep(TUTORIAL_STEPS.TRADE_CHAT_NAV);
  }, [markTutorialCompleted]);

  // When user dismisses the congrats overlay with "Got it!"
  const handleCongratsComplete = useCallback(async () => {
    const type = lastAddedType;
    setCurrentStep(TUTORIAL_STEPS.NONE);
    setIsInAddCardBinderFlow(false);
    setLastAddedType(null);
    // Mark the appropriate tutorial as completed with timestamp
    if (type === 'card') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_CARD);
    } else if (type === 'binder') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_BINDER);
    } else if (type === 'post') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_POST);
    }
  }, [lastAddedType, markTutorialCompleted]);

  // When user clicks "More Tutorials" on congrats overlay - go back to Chase's 3 options
  const handleMoreTutorials = useCallback(async () => {
    const type = lastAddedType;
    // Mark the current one as completed first
    if (type === 'card') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_CARD);
    } else if (type === 'binder') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_BINDER);
    } else if (type === 'post') {
      await markTutorialCompleted(TUTORIAL_KEYS.FIRST_POST);
    }
    setLastAddedType(null);
    setIsInAddCardBinderFlow(false);
    // Go to Chase's 3 options screen
    setCurrentStep(TUTORIAL_STEPS.YES_CHASE_HELLO);
  }, [lastAddedType, markTutorialCompleted]);

  // ===== TRADE TUTORIAL HANDLERS =====

  // When user selects "Set up a Trade" from Chase's options
  const handleStartTradeTutorial = useCallback(() => {
    setCurrentStep(TUTORIAL_STEPS.TRADE_FIND_CARD);
  }, []);

  // When user clicks "Look for Trade" button - enable filter and show arrows
  const handleLookForTrade = useCallback(() => {
    setHideMyPosts(true);
    setCurrentStep(TUTORIAL_STEPS.TRADE_LOOKING);
  }, []);

  // When user clicks a Trade button during tutorial - block it and show Chase
  const handleTradeButtonClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.TRADE_LOOKING) {
      setHideMyPosts(false); // Turn off filter
      setCurrentStep(TUTORIAL_STEPS.TRADE_BLOCKED);
      return true; // Signal that it was blocked
    }
    return false; // Not in tutorial, allow normal behavior
  }, [currentStep]);

  // When user clicks "Got it" after Trade blocked message - point to Chat
  const handleTradeBlockedContinue = useCallback(() => {
    setCurrentStep(TUTORIAL_STEPS.TRADE_CHAT_NAV);
  }, []);

  // When user clicks Chat nav during TRADE_CHAT_NAV step
  const handleTradeChatNavClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.TRADE_CHAT_NAV) {
      setCurrentStep(TUTORIAL_STEPS.TRADE_CHAT_INTRO);
    }
  }, [currentStep]);

  // When user clicks "Done" on Chat intro - end trade tutorial
  const handleTradeTutorialComplete = useCallback(async () => {
    setCurrentStep(TUTORIAL_STEPS.NONE);
    await markTutorialCompleted(TUTORIAL_KEYS.FIRST_TRADE);
  }, [markTutorialCompleted]);

  // When user finishes the Add Card/Binder tutorial (clicks either button or dismisses)
  // NOTE: This is now called when user dismisses without actually adding anything
  const handleAddCardBinderComplete = useCallback(async () => {
    setCurrentStep(TUTORIAL_STEPS.NONE);
    setIsInAddCardBinderFlow(false);
    await markTutorialCompleted(TUTORIAL_KEYS.WELCOME);
  }, [markTutorialCompleted]);

  const handleWelcomeTutorialNo = useCallback(async () => {
    // Set the next step FIRST, then hide welcome after a tiny delay
    // This prevents the flash where no overlay is visible
    setCurrentStep(TUTORIAL_STEPS.POINT_TO_PROFILE);
    setTimeout(() => {
      setShowWelcomeTutorial(false);
    }, 50);
    // Mark as shown immediately so it doesn't replay on navigation
    await markTutorialShown(TUTORIAL_KEYS.WELCOME);
  }, [markTutorialShown]);

  // When user clicks profile during point_to_profile step
  const handleProfileClicked = useCallback(() => {
    if (currentStep === TUTORIAL_STEPS.POINT_TO_PROFILE) {
      setCurrentStep(TUTORIAL_STEPS.PROFILE_FAREWELL);
      setShowProfileFarewell(true);
    }
  }, [currentStep]);

  // When user finishes the farewell on profile page
  const handleFarewellComplete = useCallback(async () => {
    setShowProfileFarewell(false);
    setCurrentStep(TUTORIAL_STEPS.NONE);
    await markTutorialDeclined(TUTORIAL_KEYS.WELCOME);
  }, [markTutorialDeclined]);

  // Check if navigation should be blocked during tutorial
  const isNavigationBlocked = BLOCKING_STEPS.includes(currentStep) || showWelcomeTutorial;

  const value = {
    // State
    tutorialProgress,
    loading,
    showWelcomeTutorial,
    currentStep,
    showProfileFarewell,
    isNavigationBlocked,
    isInAddCardBinderFlow,
    lastAddedType,
    hideMyPosts,

    // Actions
    hasTutorialBeenShown,
    markTutorialShown,
    markTutorialCompleted,
    markTutorialDeclined,
    resetAllTutorials,
    checkWelcomeTutorial,
    handleWelcomeTutorialYes,
    handleWelcomeTutorialNo,
    handleProfileClicked,
    handleFarewellComplete,
    handleIntroChaseNext,
    handleChaseOptionSelected,
    handleCardsNavClicked,
    handlePostNavClicked,
    handleAddButtonClicked,
    handleCardAdded,
    handleBinderAdded,
    handlePostAdded,
    handleTradeRequestSent,
    handleFirstTradeRequestNext,
    handleCongratsComplete,
    handleMoreTutorials,
    handleAddCardBinderComplete,
    // Trade tutorial
    handleStartTradeTutorial,
    handleLookForTrade,
    handleTradeButtonClicked,
    handleTradeBlockedContinue,
    handleTradeChatNavClicked,
    handleTradeTutorialComplete,
    setHideMyPosts, // For Profile toggle
    setCurrentStep, // For triggering tutorials from Profile

    // Refresh
    refreshProgress: fetchTutorialProgress,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};
